import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Wallet } from "lucide-react";
import { api } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { formatCurrency } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { GymLoader } from "../../components/common/GymLoader";

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"; }

// UX-TR-01 §1 SCR-P06-30: the trust-anchor screen — reads the EXACT SAME Prompt-09 payout data
// every Manager sees for this trainer (`expense_engine.get_payout_statement`), itemized down to
// the individual session. Pending (attended, not-yet-paid) sessions are shown separately with
// an explicit "delivered so far, payout pending" note — never folded into `total_paid` money.
export default function Earnings() {
  const reducedMotion = useReducedMotion();
  const earningsQ = useQuery({ queryKey: ["trainer-earnings"], queryFn: async () => (await api.get("/trainer/earnings")).data });

  if (earningsQ.isLoading) return <GymLoader fullPage label="Loading earnings…" testId="trainer-earnings-loader" />;
  const d = earningsQ.data;

  return (
    <div data-testid={TRAINER.earningsPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Earnings</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Rate: {formatCurrency(d?.commission_rule?.amount)} per session ({d?.commission_rule?.type})</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="grid grid-cols-2 gap-4">
        <div className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="eyebrow">Lifetime paid</p>
          <p data-testid={TRAINER.earningsTotalPaid} className="text-2xl font-bold mt-1 tabular-nums-force" style={{ color: "var(--scarlet-pos)" }}>{formatCurrency(d?.total_paid)}</p>
        </div>
        <div className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="eyebrow">Pending payout</p>
          <p data-testid={TRAINER.earningsPendingAmount} className="text-2xl font-bold mt-1 tabular-nums-force" style={{ color: "#141416" }}>{formatCurrency(d?.pending_amount)}</p>
          {d?.pending_note && <p className="text-xs mt-1" style={{ color: "#68686E" }}>{d.pending_note} ({d.pending_sessions_delivered})</p>}
        </div>
      </motion.div>

      {d?.pending_sessions?.length > 0 && (
        <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>Delivered, not yet paid</p>
          {d.pending_sessions.map((s) => (
            <div key={s.booking_id} data-testid={TRAINER.earningsPendingSessionRow(s.booking_id)} className="flex items-center justify-between px-4 py-2.5 text-sm" style={{ borderBottom: "1px solid #F4F3EF" }}>
              <span style={{ color: "#141416" }}>{fmtDate(s.slot?.start)} · {s.type === "pt" ? "1:1 PT" : "Class"}</span>
              <span className="tabular-nums-force" style={{ color: "#68686E" }}>{formatCurrency(s.rate)}</span>
            </div>
          ))}
        </motion.div>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}><Wallet size={14} /> Payout history</p>
        {(!d?.paid_runs || d.paid_runs.length === 0) && <p className="text-sm px-4 py-6" style={{ color: "#68686E" }}>No payouts run yet.</p>}
        {d?.paid_runs?.map((run) => (
          <div key={run.payout_run_id} data-testid={TRAINER.earningsPayoutRunRow(run.payout_run_id)} className="px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: "#141416" }}>{fmtDate(run.period?.start)} – {fmtDate(run.period?.end)} · {run.session_count} session(s)</span>
              <span className="font-semibold tabular-nums-force" style={{ color: "#141416" }}>{formatCurrency(run.commission_owed)}</span>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
